<template>
  <div class="erp-finance-container">
    <!-- 头部工具栏 -->
    <div class="erp-header">
      <div class="header-left">
        <h2 class="erp-title">
          <i class="el-icon-s-finance"></i>
          财务科目管理
        </h2>
        <div class="erp-status">
          <el-tag type="success" size="small" v-if="treeData.length > 0">
            已加载 {{ totalCount }} 个科目
          </el-tag>
          <el-tag type="info" size="small" v-else>
            未加载数据
          </el-tag>
        </div>
      </div>
      <div class="header-right">
        <div class="erp-search">
          <el-input
              v-model="searchKeyword"
              placeholder="输入关键词搜索..."
              size="mini"
              clearable
              style="width: 240px"
          >
          </el-input>
        </div>
        <div class="erp-actions">
          <el-button-group>
            <el-button
                type="warning"
                size="mini"
                icon="el-icon-refresh"
                @click="handleRefresh"
                :loading="loading"
            >
              {{ loading ? '加载中...' : '刷新' }}
            </el-button>
          </el-button-group>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="erp-main-content">
      <!-- 左侧树形导航 -->
      <div class="erp-tree-panel">
        <div class="panel-header">
          <div class="panel-title">
            <span>科目结构树</span>
          </div>
          <div class="panel-actions">
            <el-tooltip content="当前选中节点" placement="top">
              <span class="selected-info" v-if="currentNode">
                {{ currentNode.label }}
              </span>
            </el-tooltip>
            <el-dropdown @command="handleTreeCommand">
              <el-button size="mini" type="text">
                <i class="el-icon-more"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="expandAll" icon="el-icon-caret-bottom">展开全部</el-dropdown-item>
                <el-dropdown-item command="collapseAll" icon="el-icon-caret-right">折叠全部</el-dropdown-item>
                <el-dropdown-item command="expandSelected" icon="el-icon-zoom-in" :disabled="!currentNode">
                  展开选中分支
                </el-dropdown-item>
                <el-dropdown-item divided command="refresh" icon="el-icon-refresh">刷新树结构</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>

        <div class="tree-container">
          <div v-if="treeData.length === 0" class="empty-tree">
            <el-empty description="暂无科目数据" :image-size="80">
              <el-button type="primary" size="small" @click="handleAddRoot">
                创建第一个科目
              </el-button>
            </el-empty>
          </div>

          <el-tree
              v-else
              ref="financeTree"
              :data="treeData"
              node-key="key"
              :props="defaultProps"
              :default-expanded-keys="expandedKeys"
              :filter-node-method="filterNode"
              :highlight-current="true"
              :expand-on-click-node="false"
              @node-click="handleNodeClick"
              class="erp-tree"
          >
            <span class="custom-tree-node" slot-scope="{ node, data }">
              <span class="node-content">
                <i :class="getNodeIcon(data)" :style="{ color: getNodeColor(data) }"></i>
                <span class="node-label" :title="data.label">{{ node.label }}</span>
                <el-tag
                    size="mini"
                    :type="getTypeTagType(data.type)"
                    effect="plain"
                    class="type-tag"
                >
                  {{ getTypeText(data.type) }}
                </el-tag>
                <el-tag
                    v-if="data.status === 0"
                    size="mini"
                    type="info"
                    effect="plain"
                    class="status-tag"
                >
                  禁用
                </el-tag>
                <!-- 锁图标：不可编辑的节点（一级科目或系统内置） -->
                <i
                    v-if="!canEditNode(data)"
                    class="el-icon-lock root-lock-icon"
                    :title="lockReason(data)"
                ></i>
              </span>
              <span class="node-actions">
                <span class="child-count" v-if="data.children && data.children.length > 0">
                  {{ data.children.length }}
                </span>
              </span>
            </span>
          </el-tree>
        </div>
      </div>

      <!-- 右侧表格详情 -->
      <div class="erp-table-panel">
        <div class="panel-header">
          <div class="panel-title">
            <i class="el-icon-s-grid"></i>
            <span>
              {{ currentNode ? `${currentNode.label} 详情` : '科目明细' }}
              <el-tag v-if="currentNode" size="mini" type="primary">
                {{ tableData.length }} 项
              </el-tag>
            </span>
          </div>
          <div class="panel-actions">
            <el-button
                v-if="currentNode"
                type="primary"
                size="small"
                icon="el-icon-plus"
                @click="handleAddChild"
                :disabled="!canAddChild"
                :title="addButtonTooltip"
            >
              新增子项
            </el-button>
            <!-- 防御：不可编辑的节点不显示「编辑」按钮 -->
            <el-button
                v-if="currentNode && canEditNode(currentNode)"
                type="warning"
                size="small"
                icon="el-icon-edit"
                @click="handleEdit"
            >
              编辑
            </el-button>
          </div>
        </div>

        <div class="table-container">
          <!-- 空状态 -->
          <div v-if="tableData.length === 0" class="empty-table">
            <div v-if="currentNode">
              <i :class="getNodeIcon(currentNode)" style="font-size: 60px; margin-bottom: 20px;" :style="{ color: getNodeColor(currentNode) }"></i>
              <p style="color: #606266; font-size: 14px; margin-bottom: 10px;">
                {{ currentNode.hasChildren ? '该分类下暂无子项' : '此项无下级明细' }}
              </p>
              <p style="color: #909399; font-size: 12px; margin-bottom: 20px;">
                ID: {{ currentNode.id }} | 类型: {{ getTypeText(currentNode.type) }} | 状态: {{ currentNode.status === 1 ? '启用' : '禁用' }}
              </p>
              <el-button
                  v-if="currentNode.type === 1"
                  type="primary"
                  size="small"
                  @click="handleAddChild"
              >
                创建第一个子项
              </el-button>
            </div>
            <div v-else>
              <i class="el-icon-s-data" style="font-size: 60px; color: #c0c4cc; margin-bottom: 20px;"></i>
              <p style="color: #909399; margin-bottom: 10px;">请从左侧选择科目查看详情</p>
              <p style="color: #c0c4cc; font-size: 12px;">点击树节点可查看对应明细</p>
            </div>
          </div>

          <!-- 数据表格 -->
          <el-table
              v-else
              :data="tableData"
              border
              size="small"
              style="width: 100%"
              :highlight-current-row="true"
              :header-cell-style="{ background: '#f8f9fa', color: '#606266' }"
              class="erp-table"
          >
            <el-table-column label="序号" width="60" align="center" fixed>
              <template slot-scope="scope">
                <span class="row-index">{{ scope.$index + 1 }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="id" label="科目ID" width="100" align="center">
              <template slot-scope="{ row }">
                <span class="id-text">{{ row.id }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="label" label="科目名称" min-width="180">
              <template slot-scope="{ row }">
                <div class="name-cell">
                  <i :class="getNodeIcon(row)" :style="{ color: getNodeColor(row), marginRight: '6px' }"></i>
                  <span :class="getTypeClass(row.type)">{{ row.label }}</span>
                  <i v-if="!canEditNode(row)" class="el-icon-lock root-lock-icon" :title="lockReason(row)"></i>
                </div>
              </template>
            </el-table-column>

            <el-table-column prop="type" label="收支类型" width="100" align="center">
              <template slot-scope="{ row }">
                <el-tag
                    :type="getTypeTagType(row.type)"
                    size="small"
                    effect="dark"
                >
                  {{ getTypeText(row.type) }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column prop="status" label="状态" width="80" align="center">
              <template slot-scope="{ row }">
                <el-tag
                    :type="row.status === 1 ? 'success' : 'danger'"
                    size="small"
                    effect="plain"
                >
                  {{ row.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column prop="pycode" label="拼音简码" width="100" align="center">
              <template slot-scope="{ row }">
                <span class="pycode">{{ row.pycode || '-' }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="remarks" label="备注" min-width="150">
              <template slot-scope="{ row }">
                <el-tooltip :content="row.remarks" placement="top" v-if="row.remarks">
                  <span class="remarks">{{ row.remarks }}</span>
                </el-tooltip>
                <span class="no-remarks" v-else>-</span>
              </template>
            </el-table-column>

            <el-table-column label="创建时间" width="160" align="center">
              <template slot-scope="{ row }">
                <span v-if="row.createTime" class="create-time">
                  {{ formatTime(row.createTime) }}
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>

            <el-table-column label="操作" width="180" align="center" fixed="right">
              <template slot-scope="{ row }">
                <div class="action-buttons">
                  <!-- 防御：不可编辑的节点显示「已锁定」标签 -->
                  <template v-if="!canEditNode(row)">
                    <el-tooltip :content="lockReason(row)" placement="top">
                      <el-tag size="mini" type="info" effect="plain">
                        <i class="el-icon-lock"></i> 已锁定
                      </el-tag>
                    </el-tooltip>
                  </template>
                  <template v-else>
                    <el-button
                        type="primary"
                        size="mini"
                        @click="handleEditRow(row)"
                    >编辑</el-button>
                  </template>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        width="500px"
        @close="handleDialogClose"
        :close-on-click-modal="false"
    >
      <el-form
          ref="formRef"
          :model="formData"
          :rules="formRules"
          label-width="100px"
          size="small"
          class="erp-form"
          :disabled="isEdit && !canEditNode(formData)"
      >
        <!-- 上级科目（非根节点时显示） -->
        <el-form-item
            label="上级科目:"
            v-if="!isRootNode && formData.parentId !== 0 && formData.parentId !== '0'"
        >
          <el-input
              v-model="parentName"
              disabled
              placeholder="自动填充上级科目"
          >
          </el-input>
        </el-form-item>

        <!-- 科目名称 -->
        <el-form-item label="科目名称:" prop="name">
          <el-input
              v-model="formData.name"
              placeholder="请输入科目名称，2-20个字符"
              maxlength="20"
              show-word-limit
              @input="generatePycode"
              :clearable="true"
          >
          </el-input>
        </el-form-item>

        <!-- 拼音简码 -->
        <el-form-item label="拼音简码:" prop="pycode">
          <el-input
              v-model="formData.pycode"
              placeholder="自动生成拼音简码"
              maxlength="10"
              show-word-limit
              disabled
          >
          </el-input>
        </el-form-item>

        <!-- 收支类型 -->
        <el-form-item label="收支类型:" prop="type">
          <div v-if="isRootNode">
            <el-radio-group
                v-model="formData.type"
                @change="handleTypeChange"
            >
              <el-radio :label="1">
                <span class="type-option income">
                  <i class="el-icon-money"></i>
                  收入
                </span>
              </el-radio>
              <el-radio :label="2">
                <span class="type-option expense">
                  <i class="el-icon-sold-out"></i>
                  支出
                </span>
              </el-radio>
              <el-radio :label="3">
                <span class="type-option account">
                  <i class="el-icon-bank-card"></i>
                  资金账户
                </span>
              </el-radio>
            </el-radio-group>
          </div>

          <div v-else>
            <div v-if="parentType === 1">
              <div class="type-display income">
                <i class="el-icon-money"></i>
                <span>收入</span>
                <el-tag size="small" type="success" style="margin-left: 10px;">
                  继承自父级
                </el-tag>
              </div>
            </div>
            <div v-else-if="parentType === 2">
              <div class="type-display expense">
                <i class="el-icon-sold-out"></i>
                <span>支出</span>
                <el-tag size="small" type="danger" style="margin-left: 10px;">
                  继承自父级
                </el-tag>
              </div>
            </div>
            <div v-else-if="parentType === 3">
              <div class="type-display account">
                <i class="el-icon-bank-card"></i>
                <span>资金账户</span>
                <el-tag size="small" type="primary" style="margin-left: 10px;">
                  继承自父级
                </el-tag>
              </div>
            </div>
            <div v-else-if="isEdit">
              <div class="type-display" :class="getTypeClass(formData.type)">
                <i :class="getTypeIcon(formData.type)"></i>
                <span>{{ getTypeText(formData.type) }}</span>
                <el-tag size="small" type="info" style="margin-left: 10px;">
                  当前类型
                </el-tag>
              </div>
            </div>
          </div>
        </el-form-item>

        <!-- 状态 -->
        <el-form-item label="状态:" prop="status">
          <el-switch
              v-model="formData.status"
              :active-value="1"
              :inactive-value="0"
              active-text="启用"
              inactive-text="禁用"
              active-color="#13ce66"
              inactive-color="#ff4949"
              style="margin-right: 10px;"
          ></el-switch>
        </el-form-item>

        <!-- 备注 -->
        <el-form-item label="备注:">
          <el-input
              v-model="formData.remarks"
              type="textarea"
              :rows="3"
              placeholder="请输入备注信息，最多200个字符"
              maxlength="200"
              show-word-limit
              resize="none"
          ></el-input>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <div class="footer-left" v-if="isEdit">
          <span class="create-info" v-if="currentNode && currentNode.createTime">
            创建时间：{{ formatTime(currentNode.createTime) }}
          </span>
        </div>
        <div class="footer-right">
          <el-button
              @click="dialogVisible = false"
              size="small"
              :disabled="submitting"
          >
            取消
          </el-button>
          <el-button
              type="primary"
              @click="handleSubmit"
              size="small"
              :loading="submitting"
              :disabled="!formData.name"
          >
            {{ submitting ? '保存中...' : '确定保存' }}
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { addFinanceclass, editFinanceclass, getFinanceclassList } from "@/api/clasificacionContable/clasificacionContable";
import { pinyin } from "pinyin-pro";

export default {
  name: 'ErpFinanceTree',
  data() {
    return {
      parentType: null,
      dialogVisible: false,
      dialogTitle: '新增科目',
      isRootNode: false,
      isEdit: false,
      submitting: false,
      parentName: '',
      formData: {
        id: null,
        name: '',
        parentId: 0,
        pycode: '',
        remarks: '',
        status: 1,
        type: 1,
        order: 0,
        clinicId: null
      },
      formRules: {
        name: [
          { required: true, message: '请输入科目名称', trigger: 'blur' },
          { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' },
          { validator: this.validateName, trigger: 'blur' }
        ],
        type: [
          { required: true, message: '请选择收支类型', trigger: 'change' }
        ],
        order: [
          { type: 'number', min: 0, max: 999, message: '排序号必须在0-999之间', trigger: 'blur' }
        ]
      },
      treeData: [],
      tableData: [],
      currentNode: null,
      expandedKeys: [],
      loading: false,
      searchKeyword: '',
      incomeCount: 0,
      expenseCount: 0,
      activeCount: 0,
      totalCount: 0,
      maxLevel: 0,
      defaultProps: {
        children: 'children',
        label: 'label'
      }
    }
  },

  computed: {
    stats() {
      return this.calculateStats(this.treeData)
    },

    /* ============================================================ */
    /* 【规则】能否在当前选中节点下新增子项                            */
    /* - 现金：不允许新增子项（支付方式统一在聚合支付下维护）          */
    /* - 只允许在第 2 级科目下新增                                     */
    /* ============================================================ */
    canAddChild() {
      if (!this.currentNode) return false
      if (this.isNoChildAllowed(this.currentNode)) return false
      if (this.currentNode.level !== 2) return false
      return true
    },

    addButtonTooltip() {
      if (!this.currentNode) return '请先选择节点'
      if (this.isNoChildAllowed(this.currentNode)) {
        return `"${this.currentNode.label}"不允许新增子项`
      }
      if (this.currentNode.level !== 2) {
        return `当前为第${this.currentNode.level}级，只允许在第2级新增子项`
      }
      return `在"${this.currentNode.label}"下新增子项`
    }
  },

  created() {
    this.loadData()
  },

  watch: {
    searchKeyword(val) {
      if (this.$refs.financeTree) {
        this.$refs.financeTree.filter(val)
      }
    },
    treeData: {
      deep: true,
      handler() {
        this.updateStats()
      }
    }
  },

  methods: {
    /* ============================================================ */
    /* 【规则 1】一级科目：parentId 为 '0' / 0 / null / ''            */
    /* ============================================================ */
    isRootNodeRow(node) {
      if (!node) return false
      const pid = node.parentId
      return pid === '0' || pid === 0 || pid == null || pid === ''
    },

    /* ============================================================ */
    /* 【规则 2】系统内置数据：clinicId === '0'                      */
    /* ============================================================ */
    isSystemNode(node) {
      if (!node) return false
      return String(node.clinicId ?? '0') === '0'
    },

    /* ============================================================ */
    /* 【规则 3】不允许有子项的科目：现金                             */
    /* - 精准判断 id=101                                              */
    /* - 兜底：父级=资金账户(100) 且 名称=现金                         */
    /* ============================================================ */
    isNoChildAllowed(node) {
      if (!node) return false
      if (String(node.id) === '101') return true
      if (String(node.parentId) === '100' && node.label === '现金') return true
      return false
    },

    /* ============================================================ */
    /* 【规则 4】统一判断当前节点能否编辑                             */
    /* - 一级科目             → 不可编辑                              */
    /* - 系统内置(clinicId=0) → 不可编辑                              */
    /* - 本诊所数据           → 可编辑                                */
    /* ============================================================ */
    canEditNode(node) {
      if (!node) return false
      if (this.isRootNodeRow(node)) return false
      if (this.isSystemNode(node)) return false
      return true
    },

    lockReason(node) {
      if (!node) return '不允许修改'
      if (this.isRootNodeRow(node)) return '一级科目不允许修改'
      if (this.isSystemNode(node)) return '系统内置科目不允许修改'
      return '不允许修改'
    },

    getCurrentClinicId() {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        return userInfo.clinicId != null ? String(userInfo.clinicId) : ''
      } catch (e) {
        console.warn('读取 userInfo 失败:', e)
        return ''
      }
    },

    handleAddRoot() {
      this.isRootNode = true
      this.isEdit = false
      this.parentName = ''
      this.parentType = null
      this.resetForm()
      this.formData.parentId = 0
      this.formData.type = 1
      this.formData.clinicId = this.getCurrentClinicId()
      this.dialogTitle = '新增一级科目'
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate()
      })
    },

    handleAddChild() {
      if (!this.currentNode) return

      if (!this.canAddChild) {
        if (this.isNoChildAllowed(this.currentNode)) {
          this.$message.warning(`"${this.currentNode.label}"不允许新增子项`)
          return
        }
        if (this.currentNode.level !== 2) {
          this.$message.warning(`只能在第2级科目下新增子项`)
          return
        }
        return
      }

      this.isRootNode = false
      this.isEdit = false
      this.parentName = this.currentNode.label
      this.parentType = this.currentNode.type
      this.resetForm()
      this.formData.parentId = this.currentNode.id
      this.formData.type = this.currentNode.type
      this.formData.clinicId = this.getCurrentClinicId()
      this.dialogTitle = `新增子项 - ${this.currentNode.label}`
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate()
      })
    },

    /* ============================================================ */
    /* 编辑：标题、上级科目、完整回填 + 不可编辑防御                   */
    /* ============================================================ */
    handleEdit() {
      if (!this.currentNode) {
        this.$message.warning('请先选择一个科目')
        return
      }
      if (!this.canEditNode(this.currentNode)) {
        this.$message.warning(this.lockReason(this.currentNode))
        return
      }

      this.isEdit = true
      this.isRootNode = false

      this.dialogTitle = `编辑科目 - ${this.currentNode.label || this.currentNode.name || ''}`

      const parentNode = this.findParentNode(this.currentNode.id)
      this.parentType = parentNode ? parentNode.type : null
      this.parentName = parentNode ? (parentNode.label || parentNode.name || '') : ''

      this.formData = {
        id: this.currentNode.id,
        name: this.currentNode.name || this.currentNode.label || '',
        parentId: this.currentNode.parentId,
        pycode: this.currentNode.pycode || '',
        remarks: this.currentNode.remarks || '',
        status: this.currentNode.status != null ? this.currentNode.status : 1,
        type: this.currentNode.type,
        order: this.currentNode.order ?? this.currentNode.orderNum ?? 0,
        clinicId: this.currentNode.clinicId
      }

      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate()
      })
    },

    findParentNode(nodeId) {
      const findNode = (nodes, targetId) => {
        for (const node of nodes) {
          if (String(node.id) === String(targetId)) return node
          if (node.children && node.children.length > 0) {
            const found = findNode(node.children, targetId)
            if (found) return found
          }
        }
        return null
      }
      const node = findNode(this.treeData, nodeId)
      if (!node || !node.parentId) return null
      return findNode(this.treeData, node.parentId)
    },

    getTypeIcon(type) {
      switch (type) {
        case 1: return 'el-icon-money'
        case 2: return 'el-icon-sold-out'
        case 3: return 'el-icon-bank-card'
        default: return 'el-icon-question'
      }
    },

    getTypeClass(type) {
      switch (type) {
        case 1: return 'type-display income'
        case 2: return 'type-display expense'
        case 3: return 'type-display account'
        default: return 'type-display'
      }
    },

    handleEditRow(row) {
      if (!this.canEditNode(row)) {
        this.$message.warning(this.lockReason(row))
        return
      }
      this.currentNode = row
      this.handleEdit()
    },

    async handleEditStatusById(row) {
      if (!this.canEditNode(row)) {
        this.$message.warning(this.lockReason(row))
        return
      }
      const newItem = JSON.parse(JSON.stringify(row))
      newItem.status = newItem.status === 1 ? 0 : 1;
      this.currentNode = newItem
      const res = await editFinanceclass(newItem);
      if (res.code === 200) {
        await this.loadData();
        this.$message.success('修改状态成功')
      } else {
        this.$message.error(res.message + ":修改状态失败!")
      }
    },

    resetForm() {
      const keepParentId = this.formData.parentId
      const keepType = this.formData.type
      this.formData = {
        id: null,
        name: '',
        parentId: keepParentId != null ? keepParentId : 0,
        pycode: '',
        remarks: '',
        status: 1,
        type: keepType || 1,
        order: 0,
        clinicId: null
      }
    },

    generatePycode() {
      this.formData.pycode = pinyin(this.formData.name || '', {
        pattern: 'first',
        toneType: 'none',
        separator: ''
      }).toLocaleUpperCase();
    },

    handleTypeChange(value) {
      console.log('类型改变为:', this.getTypeText(value))
    },

    handleDialogClose() {
      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate()
        this.$refs.formRef.resetFields()
      }
    },

    validateName(rule, value, callback) {
      if (!value || value.trim().length < 2) {
        callback(new Error('科目名称不能少于2个字符'))
        return
      }
      const parentId = String(this.formData.parentId)
      const checkDuplicate = (nodes, name, excludeId = null) => {
        for (const node of nodes) {
          if (String(node.parentId) === parentId &&
              node.name === name &&
              String(node.id) !== String(excludeId)) {
            return true
          }
          if (node.children && node.children.length > 0) {
            if (checkDuplicate(node.children, name, excludeId)) {
              return true
            }
          }
        }
        return false
      }
      const isDuplicate = checkDuplicate(
          this.treeData,
          value.trim(),
          this.isEdit ? this.formData.id : null
      )
      if (isDuplicate) {
        callback(new Error('同一级别下已存在相同名称的科目'))
      } else {
        callback()
      }
    },

    /* ============================================================ */
    /* 提交前再次拦截                                                 */
    /* ============================================================ */
    async handleSubmit() {
      try {
        await this.$refs.formRef.validate()

        if (this.isEdit && !this.canEditNode(this.formData)) {
          this.$message.error(this.lockReason(this.formData))
          return
        }

        this.submitting = true

        const submitData = {
          name: (this.formData.name || '').trim(),
          parentId: this.formData.parentId,
          pycode: (this.formData.pycode || '').toUpperCase(),
          remarks: (this.formData.remarks || '').trim(),
          status: this.formData.status,
          type: this.formData.type,
          order: this.formData.order || 0
        }

        if (this.isEdit && this.formData.id) {
          submitData.id = this.formData.id
        }

        let res
        if (this.isEdit) {
          res = await editFinanceclass(submitData)
        } else {
          res = await addFinanceclass(submitData)
        }

        if (res.code === 200) {
          this.dialogVisible = false
          await this.loadData()
          this.$message.success(res.message || '操作成功')
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        if (error && error.message && error.message !== 'cancel') {
          this.$message.error(error.message)
        } else if (error && error.response) {
          const errorMsg = error.response.data?.message || '保存失败'
          this.$message.error(errorMsg)
        } else if (error !== 'cancel') {
          this.$message.error(this.isEdit ? '更新失败' : '新增失败')
        }
      } finally {
        this.submitting = false
      }
    },

    getTypeText(type) {
      switch (type) {
        case 1: return '收入'
        case 2: return '支出'
        case 3: return '资金账户'
        case 4: return '资产'
        case 5: return '负债'
        default: return '未知'
      }
    },

    formatTime(timeStr) {
      if (!timeStr) return '-'
      try {
        const date = new Date(timeStr)
        return date.toLocaleDateString('zh-CN') + ' ' +
            date.toLocaleTimeString('zh-CN', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })
      } catch (e) {
        return timeStr
      }
    },

    /* ============================================================ */
    /* loadData：过滤 → 规范化 → 建树 → 排序 → 恢复选中               */
    /* ============================================================ */
    async loadData() {
      try {
        this.loading = true

        const res = await getFinanceclassList()
        const rawList = Array.isArray(res) ? res : (res?.data || res?.content || [])

        if (!rawList.length) {
          this.treeData = []
          this.tableData = []
          this.currentNode = null
          this.$message.warning('暂无科目数据')
          return
        }

        /* ---------- 第 0 步：按 clinicId 过滤 ---------- */
        const currentClinicId = this.getCurrentClinicId()
        const hasClinicId = !!currentClinicId

        const filtered = hasClinicId
            ? rawList.filter(item => {
              const cid = item.clinicId != null ? String(item.clinicId) : '0'
              return cid === '0' || cid === currentClinicId
            })
            : rawList

        /* ---------- 第 1 步：规范化 ---------- */
        const list = filtered.map(item => {
          const id = String(item.id)
          const parentId = item.parentId != null ? String(item.parentId) : '0'
          const clinicId = item.clinicId != null ? String(item.clinicId) : '0'
          return {
            ...item,
            id,
            key: id,
            parentId,
            clinicId,
            label: item.name,
            text: item.name,
            name: item.name,
            type: Number(item.type) || 1,
            status: item.status != null ? Number(item.status) : 1,
            order: item.orderNum != null ? item.orderNum : (item.order || 0),
            orderNum: item.orderNum != null ? item.orderNum : (item.order || 0),
            pycode: item.pycode || '',
            remarks: item.remarks || '',
            uniCode: item.uniCode || '',
            createTime: item.createTime,
            children: []
          }
        })

        /* ---------- 第 2 步：建树（父节点不存在时提升为根） ---------- */
        const nodeMap = new Map()
        list.forEach(item => nodeMap.set(item.id, item))

        const roots = []
        list.forEach(item => {
          const pid = item.parentId
          if (!pid || pid === '0' || pid === item.id || !nodeMap.has(pid)) {
            roots.push(item)
          } else {
            nodeMap.get(pid).children.push(item)
          }
        })

        /* ---------- 第 3 步：level / hasChildren ---------- */
        const computeMeta = (nodes, level = 1) => {
          nodes.forEach(node => {
            node.level = level
            node.hasChildren = !!(node.children && node.children.length > 0)
            if (node.hasChildren) computeMeta(node.children, level + 1)
          })
        }
        computeMeta(roots, 1)

        /* ---------- 第 4 步：清空 children ---------- */
        const cleanEmpty = (nodes) => {
          nodes.forEach(node => {
            if (node.children && node.children.length > 0) {
              cleanEmpty(node.children)
            } else {
              delete node.children
            }
          })
        }
        cleanEmpty(roots)

        /* ---------- 第 5 步：排序 ---------- */
        const sortTree = (nodes) => {
          nodes.forEach(node => {
            if (node.children) sortTree(node.children)
          })
          nodes.sort((a, b) => {
            const aEmptyRoot = a.parentId === '0' && !a.hasChildren
            const bEmptyRoot = b.parentId === '0' && !b.hasChildren
            if (aEmptyRoot && !bEmptyRoot) return 1
            if (!aEmptyRoot && bEmptyRoot) return -1
            if (a.parentId === '0' && b.parentId === '0') {
              return (b.order ?? 0) - (a.order ?? 0)
            }
            return 0
          })
        }
        sortTree(roots)

        this.treeData = roots
        this.updateStats()

        /* ---------- 第 6 步：恢复选中 ---------- */
        const prevSelectedId = this.currentNode?.id
        const findNodeById = (nodes, id) => {
          for (const n of nodes) {
            if (String(n.id) === String(id)) return n
            if (n.children && n.children.length > 0) {
              const found = findNodeById(n.children, id)
              if (found) return found
            }
          }
          return null
        }

        const target = prevSelectedId ? findNodeById(roots, prevSelectedId) : null
        const defaultNode = target || roots[0]

        if (defaultNode) {
          this.expandedKeys = roots.map(n => n.key)
          this.currentNode = defaultNode
          this.updateTableData()
          this.$nextTick(() => {
            if (this.$refs.financeTree) {
              this.$refs.financeTree.setCurrentKey(defaultNode.key)
            }
          })
        } else {
          this.currentNode = null
          this.tableData = []
        }

        this.$message.success(`数据加载成功，共 ${this.totalCount} 个科目`)
      } catch (error) {
        console.error('加载失败:', error)
        this.$message.error('系统异常: ' + error.message)
      } finally {
        this.loading = false
      }
    },

    updateStats() {
      const stats = this.calculateStats(this.treeData)
      this.incomeCount = stats.incomeCount
      this.expenseCount = stats.expenseCount
      this.activeCount = stats.activeCount
      this.totalCount = stats.totalCount
      this.maxLevel = stats.maxLevel
    },

    calculateStats(nodes) {
      let incomeCount = 0
      let expenseCount = 0
      let activeCount = 0
      let totalCount = 0
      let maxLevel = 0

      const traverse = (items, level) => {
        items.forEach(item => {
          totalCount++
          maxLevel = Math.max(maxLevel, level)
          if (item.type === 1) incomeCount++
          if (item.type === 2) expenseCount++
          if (item.status === 1) activeCount++
          if (item.children && item.children.length > 0) {
            traverse(item.children, level + 1)
          }
        })
      }

      traverse(nodes, 1)
      return { incomeCount, expenseCount, activeCount, totalCount, maxLevel }
    },

    updateTableData() {
      if (!this.currentNode) {
        this.tableData = []
        return
      }
      if (this.currentNode.children && this.currentNode.children.length > 0) {
        this.tableData = [...this.currentNode.children]
      } else {
        this.tableData = []
      }
    },

    handleNodeClick(data) {
      this.currentNode = data
      this.updateTableData()
    },

    handleSearch() {
      if (this.$refs.financeTree) {
        this.$refs.financeTree.filter(this.searchKeyword)
      }
    },

    clearSearch() {
      this.searchKeyword = ''
      if (this.$refs.financeTree) {
        this.$refs.financeTree.filter('')
      }
    },

    handleTreeCommand(command) {
      switch (command) {
        case 'expandAll': this.expandAll(); break
        case 'collapseAll': this.collapseAll(); break
        case 'expandSelected': this.expandSelected(); break
        case 'refresh': this.handleRefresh(); break
      }
    },

    expandAll() {
      const store = this.$refs.financeTree?.store?.nodesMap
      if (!store) return
      Object.keys(store).forEach(key => {
        store[key].expanded = true
      })
    },

    collapseAll() {
      const store = this.$refs.financeTree?.store?.nodesMap
      if (!store) return
      Object.keys(store).forEach(key => {
        store[key].expanded = false
      })
    },

    expandSelected() {
      if (!this.currentNode || !this.$refs.financeTree) return
      const expandParents = (nodeKey) => {
        const node = this.$refs.financeTree.store.nodesMap[nodeKey]
        if (node && node.parent) {
          node.parent.expanded = true
          expandParents(node.parent.key)
        }
      }
      expandParents(this.currentNode.key)
      const self = this.$refs.financeTree.store.nodesMap[this.currentNode.key]
      if (self) self.expanded = true
    },

    handleRefresh() {
      this.loadData()
    },

    getNodeIcon(data) {
      if (data.hasChildren) {
        return data.status === 1 ? 'el-icon-folder-opened' : 'el-icon-folder'
      }
      return data.status === 1 ? 'el-icon-document' : 'el-icon-document-checked'
    },

    getNodeColor(data) {
      if (data.status === 0) return '#C0C4CC'
      switch (data.type) {
        case 1: return '#67C23A'
        case 2: return '#F56C6C'
        case 3: return '#409EFF'
        case 4: return '#ff8000'
        case 5: return '#909399'
        default: return '#909399'
      }
    },

    getTypeTagType(type) {
      switch (type) {
        case 1: return 'success'
        case 2: return 'danger'
        case 3: return 'primary'
        case 4: return 'warning'
        case 5: return 'info'
        default: return 'info'
      }
    },

    filterNode(value, data) {
      if (!value) return true
      return (
          (data.label && data.label.toLowerCase().includes(value.toLowerCase())) ||
          (data.pycode && data.pycode.toLowerCase().includes(value.toLowerCase())) ||
          (data.remarks && data.remarks.toLowerCase().includes(value.toLowerCase()))
      )
    }
  }
}
</script>

<style lang="less" scoped>
/deep/ .el-dialog__header {
  padding: 12px;
  background-color: #007bb0;
}
/deep/ .el-dialog__title {
  font-size: 12px;
  color: #fff;
}
/deep/ .el-dialog__headerbtn .el-dialog__close {
  color: #fff;
}
/deep/ .el-input.is-disabled .el-input__inner {
  color: #87898f;
}
/deep/ .el-textarea.is-disabled .el-textarea__inner {
  color: #87898f;
}
.patient-dialog {
  .el-dialog {
    max-height: 70vh !important;
    height: auto !important;
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 15% !important;
    left: 50%;
    transform: translateX(-50%);
    margin: 0 !important;
    .el-dialog__header {
      height: 35px !important;
      padding: 5px 15px !important;
      flex-shrink: 0;
      .el-dialog__title {
        font-size: 12px !important;
        line-height: 25px !important;
      }
    }
    .el-dialog__body {
      flex: 1;
      padding: 8px !important;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 0;
      .aform, .bform {
        flex: 1;
        overflow-y: auto;
        margin-bottom: 5px;
        min-height: 0;
        .el-tabs {
          height: 100%;
          display: flex;
          flex-direction: column;
          min-height: 0;
          .el-tabs__content {
            flex: 1;
            overflow: visible;
            padding: 5px;
          }
        }
      }
    }
    .el-dialog__footer {
      height: 40px !important;
      padding: 5px !important;
      flex-shrink: 0;
      .el-button {
        padding: 7px 15px !important;
      }
    }
  }
  @media (max-height: 600px) {
    .el-dialog {
      max-height: 80vh !important;
      top: 10% !important;
      .el-dialog__body {
        padding: 5px !important;
      }
    }
  }
}
.erp-finance-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  padding: 20px;

  .erp-header {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .header-left {
      .erp-title {
        margin: 0;
        font-size: 20px;
        color: #303133;
        display: flex;
        align-items: center;
        gap: 10px;
        i {
          font-size: 24px;
          color: #409EFF;
        }
      }
      .erp-status {
        margin-top: 8px;
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 20px;
      .erp-search {
        /deep/ .el-input-group__append {
          background-color: #409EFF;
          border-color: #409EFF;
          .el-button {
            color: white;
          }
        }
      }
      .erp-actions {
        .el-button-group {
          .el-button {
            border-radius: 4px;
            margin-left: 0;
            &.is-disabled {
              opacity: 0.6;
            }
          }
        }
      }
    }
  }

  .erp-main-content {
    flex: 1;
    display: flex;
    gap: 20px;
    overflow: hidden;

    .erp-tree-panel {
      width: 20%;
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;

      .panel-header {
        padding: 16px 20px;
        border-bottom: 1px solid #ebeef5;
        display: flex;
        justify-content: space-between;
        align-items: center;
        .panel-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #303133;
          i { color: #409EFF; }
        }
        .panel-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          .selected-info {
            font-size: 12px;
            color: #409EFF;
            max-width: 120px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }

      .tree-container {
        flex: 1;
        padding: 10px;
        overflow-y: auto;
        .empty-tree {
          padding: 40px 20px;
        }
        .erp-tree {
          /deep/ .el-tree-node {
            .el-tree-node__content {
              height: 40px;
              border-radius: 4px;
              transition: all 0.2s ease;
              &:hover {
                background-color: #f5f7fa;
              }
            }
            &.is-current {
              > .el-tree-node__content {
                background: linear-gradient(135deg, #ecf5ff, #d9ecff);
                border-left: 3px solid #409EFF;
                .node-label {
                  font-weight: 600;
                  color: #409EFF;
                }
              }
            }
          }
          .custom-tree-node {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 14px;
            .node-content {
              display: flex;
              align-items: center;
              flex: 1;
              min-width: 0;
              i {
                margin-right: 8px;
                flex-shrink: 0;
              }
              .node-label {
                flex: 1;
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
              .type-tag,
              .status-tag {
                margin-left: 4px;
                flex-shrink: 0;
              }
              .root-lock-icon {
                margin-left: 6px;
                color: #909399;
                font-size: 12px;
                cursor: help;
              }
            }
            .node-actions {
              .child-count {
                background: #f0f9ff;
                color: #409EFF;
                padding: 2px 8px;
                border-radius: 10px;
                font-size: 12px;
                font-weight: 500;
              }
            }
          }
        }
      }
    }

    .erp-table-panel {
      flex: 1;
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      min-width: 0;

      .panel-header {
        padding: 16px 20px;
        border-bottom: 1px solid #ebeef5;
        display: flex;
        justify-content: space-between;
        align-items: center;
        .panel-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #303133;
          i { color: #409EFF; }
        }
        .panel-actions {
          display: flex;
          gap: 8px;
        }
      }

      .table-container {
        flex: 1;
        padding: 20px;
        overflow-y: auto;
        .empty-table {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #909399;
          padding: 40px 20px;
          text-align: center;
        }
        .erp-table {
          /deep/ .el-table__header-wrapper {
            th { font-weight: 600; }
          }
          /deep/ .el-table__body-wrapper {
            tr {
              &:hover {
                td { background-color: #f8f9fa; }
              }
            }
          }
          .row-index { color: #909399; font-weight: 500; }
          .id-text { color: #409EFF; font-weight: 500; }
          .name-cell {
            display: flex;
            align-items: center;
            .root-lock-icon {
              margin-left: 6px;
              color: #909399;
              font-size: 12px;
              cursor: help;
            }
          }
          .income-text { color: #67C23A; font-weight: 500; }
          .expense-text { color: #F56C6C; font-weight: 500; }
          .account-text { color: #409EFF; font-weight: 500; }
          .order-text { color: #E6A23C; font-weight: 500; }
          .pycode {
            font-family: 'Courier New', monospace;
            color: #909399;
          }
          .remarks {
            display: inline-block;
            max-width: 150px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: #606266;
          }
          .no-remarks {
            color: #C0C4CC;
            font-style: italic;
          }
          .create-time {
            color: #909399;
            font-size: 12px;
          }
        }
      }
    }
  }
}

@media (max-width: 1200px) {
  .erp-main-content {
    flex-direction: column;
    .erp-tree-panel {
      width: 100%;
      max-height: 400px;
    }
  }
}

.tree-container::-webkit-scrollbar,
.table-container::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.tree-container::-webkit-scrollbar-track,
.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}
.tree-container::-webkit-scrollbar-thumb,
.table-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}
.tree-container::-webkit-scrollbar-thumb:hover,
.table-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>